// <s-product-slider> shows one product image at a time. Its [data-slider-track] is a
// horizontal snap scroller (swipe works natively); [data-slider-prev]/[data-slider-next]
// buttons move one slide, and [data-slider-current] shows the visible slide number.
class ProductSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-slider-track]");
    if (!this.track) return;
    this.prev = this.querySelector("[data-slider-prev]");
    this.next = this.querySelector("[data-slider-next]");
    this.current = this.querySelector("[data-slider-current]");
    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.onScroll = () => this.sync();
    this.prev?.addEventListener("click", this.onPrev);
    this.next?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    // Variant images: when a shopper picks a variant (e.g. a colour) that has its own
    // image, jump to that image. The map comes from [data-variant-media] JSON.
    this.variantMedia = {};
    try {
      const raw = this.querySelector("[data-variant-media]")?.textContent;
      if (raw) this.variantMedia = JSON.parse(raw);
    } catch (error) {
      this.variantMedia = {};
    }
    this.onVariantChange = (event) => {
      const input = event.target;
      const picker = input?.closest?.("s-variant-picker");
      if (!picker || picker.dataset.productId !== this.dataset.productId) return;
      const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) this.showMedia(this.variantMedia[variantId], true);
    };
    document.addEventListener("change", this.onVariantChange);
    if (this.dataset.initialMediaId) this.showMedia(this.dataset.initialMediaId, false);

    this.sync();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener("click", this.onPrev);
    this.next?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("change", this.onVariantChange);
  }

  showMedia(mediaId, animate) {
    if (!mediaId) return;
    const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (target < 0 || target === this.index()) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const width = this.track.clientWidth;
    const dir = getComputedStyle(this.track).direction === "rtl" ? -1 : 1;
    // Scroll the track itself so the page doesn't jump vertically.
    this.track.scrollTo({
      left: dir * target * width,
      behavior: animate && !reduce ? "smooth" : "auto",
    });
  }

  get slides() {
    return Array.from(this.track.children);
  }

  index() {
    const width = this.track.clientWidth || 1;
    const offset = Math.abs(this.track.scrollLeft);
    return Math.min(this.slides.length - 1, Math.round(offset / width));
  }

  go(step) {
    const count = this.slides.length;
    if (count < 2) return;
    // Wrap around so the arrows always change the image.
    const target = (this.index() + step + count) % count;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.slides[target].scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "nearest",
      inline: "start",
    });
  }

  sync() {
    if (this.current) this.current.textContent = String(this.index() + 1);
  }
}

if (!customElements.get("s-product-slider")) {
  customElements.define("s-product-slider", ProductSlider);
}
